import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import JobDetailsSection from './components/JobDetailsSection';
import LocationSection from './components/LocationSection';
import DescriptionEditor from './components/DescriptionEditor';
import SkillsSection from './components/SkillsSection';
import ApplicationSettings from './components/ApplicationSettings';
import PostingOptions from './components/PostingOptions';
import JobPreview from './components/JobPreview';
import { jobService } from '../../services/jobService';
import { paymentService } from '../../services/paymentService';
import { useAuth } from '../../contexts/AuthContext';
import JobPaymentForm from '../../components/payment/JobPaymentForm';

const PostNewJob = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showPreview, setShowPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');

  const [formData, setFormData] = useState({
    jobTitle: '',
    department: '',
    employmentType: '',
    experienceLevel: '',
    salaryRange: '',
    openings: '1',
    deadline: '',
    workMode: '',
    locations: [{ city: '', state: '', isRemote: false }],
    allowRelocation: false,
    responsibilities: '',
    requirements: '',
    qualifications: '',
    skills: [],
    screeningQuestions: [],
    documentRequirements: [],
    autoConfirmation: true,
    allowDraft: true,
    instantNotification: false,
    postingType: 'standard',
    duration: '30',
    socialPlatforms: [],
    urgentHiring: false,
    hideCompany: false
  });

  const [errors, setErrors] = useState({});
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [createdJobId, setCreatedJobId] = useState(null);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData?.jobTitle?.trim()) newErrors.jobTitle = 'Job title is required';
      if (!formData?.department) newErrors.department = 'Department is required';
      if (!formData?.employmentType) newErrors.employmentType = 'Employment type is required';
      if (!formData?.experienceLevel) newErrors.experienceLevel = 'Experience level is required';
      if (!formData?.salaryRange) newErrors.salaryRange = 'Salary range is required';
      if (!formData?.openings || formData?.openings < 1) newErrors.openings = 'Number of openings must be at least 1';
      if (!formData?.deadline) newErrors.deadline = 'Application deadline is required';
    }

    if (step === 2) {
      if (!formData?.workMode) newErrors.workMode = 'Work mode is required';
      if (formData?.workMode !== 'remote') {
        if (!formData?.locations?.[0]?.city) newErrors.locations = 'At least one location is required';
      }
    }

    if (step === 3) {
      if (!formData?.responsibilities?.trim()) newErrors.responsibilities = 'Job responsibilities are required';
      if (!formData?.requirements?.trim()) newErrors.requirements = 'Job requirements are required';
    }

    if (step === 4) {
      if (!formData?.skills || formData?.skills?.length === 0) {
        newErrors.skills = 'At least one skill is required';
      }
    }

    if (step === 6) {
      if (!formData?.postingType) newErrors.postingType = 'Posting type is required';
      if (!formData?.duration) newErrors.duration = 'Posting duration is required';
    }

    setErrors(newErrors);
    
    // Set validation error message if there are errors
    if (Object.keys(newErrors)?.length > 0) {
      setValidationError(`Please fill in all required fields before proceeding to the next step.`);
    } else {
      setValidationError('');
    }
    
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < 6) {
        setCurrentStep(currentStep + 1);
        setValidationError('');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else {
      // Scroll to top to show validation error banner
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSaveDraft = async () => {
    try {
      setIsSaving(true);
      setError('');
      
      // Parse salary range
      const salaryParts = formData?.salaryRange?.split('-')?.map((s) => s?.trim());
      const salaryMin = salaryParts?.[0] ? parseInt(salaryParts?.[0]?.replace(/[^0-9]/g, '')) : null;
      const salaryMax = salaryParts?.[1] ? parseInt(salaryParts?.[1]?.replace(/[^0-9]/g, '')) : null;
      
      // Calculate expiry date
      const deadlineDate = new Date(formData?.deadline);
      const expiryDate = new Date(deadlineDate);
      expiryDate?.setDate(expiryDate?.getDate() + parseInt(formData?.duration || '30'));
      
      const jobData = {
        jobTitle: formData?.jobTitle,
        department: formData?.department,
        employmentType: formData?.employmentType,
        experienceLevel: formData?.experienceLevel,
        salaryMin,
        salaryMax,
        openings: parseInt(formData?.openings || '1'),
        workMode: formData?.workMode,
        city: formData?.locations?.[0]?.city || null,
        state: formData?.locations?.[0]?.state || null,
        allowRelocation: formData?.allowRelocation || false,
        responsibilities: formData?.responsibilities,
        requirements: formData?.requirements,
        qualifications: formData?.qualifications || '',
        skills: formData?.skills || [],
        screeningQuestions: formData?.screeningQuestions || [],
        documentRequirements: formData?.documentRequirements || [],
        autoConfirmation: formData?.autoConfirmation || true,
        instantNotification: formData?.instantNotification || false,
        postingType: formData?.postingType || 'standard',
        urgentHiring: formData?.urgentHiring || false,
        hideCompany: formData?.hideCompany || false,
        deadline: deadlineDate?.toISOString(),
        expiryDate: expiryDate?.toISOString(),
        status: 'draft'
      };
      
      await jobService?.createJob(jobData);
      alert('Draft saved successfully! You can continue editing later.');
    } catch (err) {
      console.error('Error saving draft:', err);
      setError(err?.message || 'Failed to save draft');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;
    
    try {
      setIsSaving(true);
      setError('');
      
      // Parse salary range
      const salaryParts = formData?.salaryRange?.split('-')?.map((s) => s?.trim());
      const salaryMin = salaryParts?.[0] ? parseInt(salaryParts?.[0]?.replace(/[^0-9]/g, '')) : null;
      const salaryMax = salaryParts?.[1] ? parseInt(salaryParts?.[1]?.replace(/[^0-9]/g, '')) : null;
      
      // Calculate expiry date
      const deadlineDate = new Date(formData?.deadline);
      const expiryDate = new Date(deadlineDate);
      expiryDate?.setDate(expiryDate?.getDate() + parseInt(formData?.duration || '30'));
      
      const jobData = {
        jobTitle: formData?.jobTitle,
        department: formData?.department,
        employmentType: formData?.employmentType,
        experienceLevel: formData?.experienceLevel,
        salaryMin,
        salaryMax,
        openings: parseInt(formData?.openings || '1'),
        workMode: formData?.workMode,
        city: formData?.locations?.[0]?.city || null,
        state: formData?.locations?.[0]?.state || null,
        allowRelocation: formData?.allowRelocation || false,
        responsibilities: formData?.responsibilities,
        requirements: formData?.requirements,
        qualifications: formData?.qualifications || '',
        skills: formData?.skills || [],
        screeningQuestions: formData?.screeningQuestions || [],
        documentRequirements: formData?.documentRequirements || [],
        autoConfirmation: formData?.autoConfirmation || true,
        instantNotification: formData?.instantNotification || false,
        postingType: formData?.postingType || 'standard',
        urgentHiring: formData?.urgentHiring || false,
        hideCompany: formData?.hideCompany || false,
        deadline: deadlineDate?.toISOString(),
        expiryDate: expiryDate?.toISOString(),
        status: 'active'
      };
      
      // Create the job first
      const createdJob = await jobService?.createJob(jobData);
      setCreatedJobId(createdJob?.id);

      // Check if payment is required
      if (formData?.postingType && formData?.postingType !== 'standard') {
        // Initiate payment flow
        const employerInfo = {
          userId: user?.id,
          email: user?.email,
          firstName: user?.user_metadata?.first_name || '',
          lastName: user?.user_metadata?.last_name || '',
          phone: user?.user_metadata?.phone || '',
          stripeCustomerId: user?.user_metadata?.stripe_customer_id || null
        };

        const paymentIntent = await paymentService?.createJobPaymentIntent(
          createdJob?.id,
          formData?.postingType,
          employerInfo
        );

        setPaymentData(paymentIntent);
        setShowPaymentModal(true);
      } else {
        // Standard posting - no payment needed
        setShowSuccessModal(true);
      }
    } catch (err) {
      console.error('Error publishing job:', err);
      setError(err?.message || 'Failed to publish job');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePaymentSuccess = (result) => {
    setShowPaymentModal(false);
    setShowSuccessModal(true);
  };

  const handlePaymentError = (error) => {
    setError(error?.message || 'Payment failed. Please try again.');
  };

  const handlePaymentCancel = () => {
    setShowPaymentModal(false);
    setError('Payment cancelled. You can complete payment later from your dashboard.');
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    navigate('/employer-dashboard');
  };

  const steps = [
    { number: 1, title: 'Job Details', icon: 'Briefcase' },
    { number: 2, title: 'Location', icon: 'MapPin' },
    { number: 3, title: 'Description', icon: 'FileEdit' },
    { number: 4, title: 'Skills', icon: 'Code2' },
    { number: 5, title: 'Application', icon: 'Settings' },
    { number: 6, title: 'Posting', icon: 'Rocket' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <button
              onClick={() => navigate('/employer-dashboard')}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-smooth mb-4"
            >
              <Icon name="ArrowLeft" size={20} />
              <span className="text-sm font-medium">Back to Dashboard</span>
            </button>
            <h1 className="text-3xl font-bold text-foreground mb-2">Post a New Job</h1>
            <p className="text-muted-foreground">Create a comprehensive job listing to attract qualified candidates</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
                <div>
                  <h4 className="text-sm font-semibold text-destructive mb-1">Error</h4>
                  <p className="text-sm text-muted-foreground">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Validation Error Banner */}
          {validationError && (
            <div className="bg-warning/10 border border-warning/30 rounded-lg p-4 mb-6 animate-in fade-in duration-200">
              <div className="flex items-start gap-3">
                <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-warning mb-1">Missing Required Information</h4>
                  <p className="text-sm text-muted-foreground">{validationError}</p>
                  {Object.keys(errors)?.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {Object.entries(errors)?.map(([field, message]) => (
                        <li key={field} className="text-sm text-muted-foreground flex items-center gap-2">
                          <Icon name="AlertCircle" size={14} color="var(--color-warning)" />
                          {message}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <button
                  onClick={() => setValidationError('')}
                  className="text-muted-foreground hover:text-foreground transition-smooth"
                >
                  <Icon name="X" size={18} />
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-1">
              <div className="bg-card rounded-lg border border-border p-6 sticky top-24">
                <h3 className="text-sm font-semibold text-foreground mb-4">Progress</h3>
                <div className="space-y-3">
                  {steps?.map((step) => (
                    <button
                      key={step?.number}
                      onClick={() => {
                        if (step?.number < currentStep || validateStep(currentStep)) {
                          setCurrentStep(step?.number);
                        }
                      }}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg transition-smooth ${
                        currentStep === step?.number
                          ? 'bg-primary text-primary-foreground'
                          : currentStep > step?.number
                          ? 'bg-success/10 text-success hover:bg-success/20' :'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        currentStep === step?.number
                          ? 'bg-primary-foreground/20'
                          : currentStep > step?.number
                          ? 'bg-success/20' :'bg-background/20'
                      }`}>
                        {currentStep > step?.number ? (
                          <Icon name="Check" size={16} />
                        ) : (
                          <Icon name={step?.icon} size={16} />
                        )}
                      </div>
                      <div className="text-left flex-1">
                        <div className="text-xs opacity-80">Step {step?.number}</div>
                        <div className="text-sm font-medium">{step?.title}</div>
                      </div>
                    </button>
                  ))}
                </div>

                <div className="mt-6 pt-6 border-t border-border">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-muted-foreground">Completion</span>
                    <span className="font-medium text-foreground">{Math.round((currentStep / steps?.length) * 100)}%</span>
                  </div>
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all duration-300"
                      style={{ width: `${(currentStep / steps?.length) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-3 space-y-6">
              {currentStep === 1 && (
                <JobDetailsSection
                  formData={formData}
                  onChange={handleChange}
                  errors={errors}
                />
              )}

              {currentStep === 2 && (
                <LocationSection
                  formData={formData}
                  onChange={handleChange}
                  errors={errors}
                />
              )}

              {currentStep === 3 && (
                <DescriptionEditor
                  formData={formData}
                  onChange={handleChange}
                  errors={errors}
                />
              )}

              {currentStep === 4 && (
                <SkillsSection
                  formData={formData}
                  onChange={handleChange}
                  errors={errors}
                />
              )}

              {currentStep === 5 && (
                <ApplicationSettings
                  formData={formData}
                  onChange={handleChange}
                  errors={errors}
                />
              )}

              {currentStep === 6 && (
                <PostingOptions
                  formData={formData}
                  onChange={handleChange}
                  errors={errors}
                />
              )}

              <div className="bg-card rounded-lg border border-border p-6">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    {currentStep > 1 && (
                      <Button
                        variant="outline"
                        iconName="ChevronLeft"
                        iconPosition="left"
                        onClick={handlePrevious}
                      >
                        Previous
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      iconName="Save"
                      iconPosition="left"
                      onClick={handleSaveDraft}
                      loading={isSaving}
                    >
                      Save Draft
                    </Button>
                  </div>

                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline"
                      iconName="Eye"
                      iconPosition="left"
                      onClick={() => setShowPreview(true)}
                    >
                      Preview
                    </Button>
                    {currentStep < 6 ? (
                      <Button
                        variant="default"
                        iconName="ChevronRight"
                        iconPosition="right"
                        onClick={handleNext}
                      >
                        Next Step
                      </Button>
                    ) : (
                      <Button
                        variant="default"
                        iconName="Send"
                        iconPosition="left"
                        onClick={handleSubmit}
                        loading={isSaving}
                      >
                        Publish Job
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showPreview && (
        <JobPreview
          formData={formData}
          onClose={() => setShowPreview(false)}
        />
      )}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
          <div className="bg-card rounded-lg border border-border p-8 max-w-md w-full text-center">
            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="CheckCircle2" size={32} color="var(--color-success)" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">Job Posted Successfully!</h2>
            <p className="text-muted-foreground mb-6">
              Your job posting is now live and visible to candidates. You'll start receiving applications soon.
            </p>
            <div className="space-y-3">
              <Button
                variant="default"
                className="w-full"
                onClick={handleSuccessClose}
              >
                Go to Dashboard
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setShowSuccessModal(false);
                  window.location?.reload();
                }}
              >
                Post Another Job
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && paymentData && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
          <div className="bg-card rounded-lg border border-border p-8 max-w-2xl w-full">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-foreground mb-2">Complete Payment</h2>
              <p className="text-muted-foreground">
                Secure payment processing for your {formData?.postingType} job posting
              </p>
            </div>

            <JobPaymentForm
              clientSecret={paymentData?.clientSecret}
              amount={paymentData?.amount}
              postingType={paymentData?.postingType}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
              onCancel={handlePaymentCancel}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PostNewJob;